import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword 
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const TOKEN_KEY = 'firebase_auth_token';
const USER_KEY = 'admin_user';

export const firebaseAuthService = {
  // Connexion admin avec email/password
  login: async ({ username, password }) => {
    try {
      // Dans Firebase, on utilise l'email comme identifiant
      // Si username n'est pas un email, on peut le convertir
      const email = username.includes('@') ? username : `${username}@bpara-admin.local`;
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // 🚀 FORCER LE RAFRAÎCHISSEMENT DU TOKEN pour récupérer les claims
      console.log("Rafraîchissement du token...");
      await user.getIdToken(true);
      console.log("Token rafraîchi avec succès");
      
      // Obtenir le token avec les claims mis à jour
      const token = await user.getIdToken();
      const tokenResult = await user.getIdTokenResult();
      
      // Vérifier les custom claims (seule source de vérité pour le rôle admin :
      // ce claim ne peut être posé que côté serveur via addAdminClaim.js / Admin SDK)
      const isAdminFromClaims = tokenResult.claims.admin === true;
      
      if (!isAdminFromClaims) {
        await signOut(auth);
        throw new Error('Access denied: Admin privileges required');
      }
      
      console.log("Admin détecté via Custom Claims");
      
      // Stocker les informations localement
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify({
        uid: user.uid,
        email: user.email,
        username: email.split('@')[0],
        role: 'ADMIN'
      }));
      
      return {
        uid: user.uid,
        email: user.email,
        username: email.split('@')[0],
        role: 'ADMIN'
      };
    } catch (error) {
      console.error('Login error:', error);
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        throw new Error('Invalid credentials');
      }
      throw error;
    }
  },

  // Déconnexion
  logout: async () => {
    try {
      await signOut(auth);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  },

  // Obtenir le token actuel
  getToken: () => localStorage.getItem(TOKEN_KEY),

  // Obtenir l'utilisateur actuel
  getUser: () => {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  },

  // Vérifier si l'utilisateur est authentifié
  isAuthenticated: () => !!localStorage.getItem(TOKEN_KEY),

  // Écouter les changements d'état d'authentification
  onAuthStateChange: (callback) => {
    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Vérifier les custom claims (seule source de vérité pour le rôle admin)
          const tokenResult = await user.getIdTokenResult();
          const isAdminFromClaims = tokenResult.claims.admin === true;
          
          if (isAdminFromClaims) {
            const token = await user.getIdToken();
            
            localStorage.setItem(TOKEN_KEY, token);
            localStorage.setItem(USER_KEY, JSON.stringify({
              uid: user.uid,
              email: user.email,
              username: user.email.split('@')[0],
              role: 'ADMIN'
            }));
            
            callback({
              uid: user.uid,
              email: user.email,
              username: user.email.split('@')[0],
              role: 'ADMIN'
            });
            return;
          }
          
          // Pas de claim admin : utilisateur authentifié mais sans privilèges admin
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          callback(null);
        } catch (error) {
          console.error('Error checking admin status:', error);
          callback(null);
        }
      } else {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        callback(null);
      }
    });
  },

  // Créer un compte admin (pour l'initialisation)
  createAdmin: async (email, password, username) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Créer le document admin dans Firestore
      await setDoc(doc(db, 'admins', user.uid), {
        username,
        email,
        role: 'ADMIN',
        createdAt: new Date(),
        isActive: true
      });
      
      return {
        uid: user.uid,
        email: user.email,
        username,
        role: 'ADMIN'
      };
    } catch (error) {
      console.error('Error creating admin:', error);
      throw error;
    }
  }
};